import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, PlusOutlined, RightOutlined } from '@fx-ui/fine-design';
import { cloneDeep, compact, filter, get, includes, isEmpty, isNumber, map } from '@libs/shared';
import { getResolverType } from '../../../utils';
import { CheckItem, CustomCombo } from '../../../components';
import { Intl } from '../../../constants';
import {
    BaseArgumentValue,
    IFunctionArgumentFrom,
    ParameterMatchMetadata,
    PLUGIN_ARGUMENT_FIELD_TYPE,
    PLUGIN_ARGUMENT_VALUE_FROM,
    PluginArgumentField,
    VectorArgumentField,
    VectorArgumentValue,
    VectorIteratorType
} from '../../../interfaces';
import PluginComponent, { PluginComponentContext } from '../plugin-component';
import { MapVector, RowEditor } from './components';
import FieldVariableSelect from '../field-variable-select';

interface IPluginVectorProps {
    resolverTypeList: IFunctionArgumentFrom[];
    /** 所有参数的匹配元数据（用于为子字段显示匹配状态） */
    autoFillMetadata?: Record<string, ParameterMatchMetadata>;
}

/**
 * 插件-子表单
 * @param props
 */
const PluginVector: React.FC<IPluginVectorProps> = (props) => {
    const { resolverTypeList, autoFillMetadata } = props;

    const [expandRowIndex, setExpandRowIndex] = useState<number>();

    const pluginComponentStore = useContext(PluginComponentContext);

    const assignmentComboItems = useMemo(() => {
        return compact([
            pluginComponentStore?.supportVectorLoop && {
                text: Intl.get('plugin.vector.assignment.loop'),
                value: VectorIteratorType.loop
            },
            {
                text: Intl.get('plugin.vector.assignment.map'),
                value: VectorIteratorType.map
            }
        ]);
    }, [pluginComponentStore?.supportVectorLoop]);

    const optionalSubFields = useMemo(() => {
        const subformWidgetName = (pluginComponentStore?.fieldValue as VectorArgumentValue)
            ?.resolver_ctx?.[1];
        if (!subformWidgetName) {
            return [];
        }
        return filter(pluginComponentStore?.optionalFields, (item) => {
            return includes(item.from, subformWidgetName);
        });
    }, [
        pluginComponentStore?.optionalFields,
        (pluginComponentStore?.fieldValue as VectorArgumentValue)?.resolver_ctx
    ]);

    if (!pluginComponentStore) {
        return null;
    }

    const {
        field,
        fieldValue,
        isView,
        defaultResolverType = PLUGIN_ARGUMENT_VALUE_FROM.LITERAL,
        checkTipVisible = false,
        onChange
    } = pluginComponentStore;

    const { fieldConf } = field;
    const vectorFields: PluginArgumentField[] = get(fieldConf, 'fields', []);
    // vector强制规定的列resolver_type
    const vectorResolver: Record<string, PLUGIN_ARGUMENT_VALUE_FROM> = get(fieldConf, 'resolvers');
    const { resolver_arg, iterator_type, resolver_ctx } = fieldValue as VectorArgumentValue;
    const isLoop = iterator_type === VectorIteratorType.loop;

    const getLineDefaultValue = (
        handleResolverType?: (colIndex: number) => PLUGIN_ARGUMENT_VALUE_FROM
    ) => {
        return map(vectorFields, (v, index) => {
            return {
                id: v.id,
                type: v.fieldType,
                resolver_type: handleResolverType
                    ? handleResolverType(index)
                    : getResolverType(v, false, get(vectorResolver, v.id, defaultResolverType)),
                resolver_arg: v.defaultValue
            };
        }) as BaseArgumentValue[];
    };

    const handleAdd = () => {
        const temp = (resolver_arg || []).slice(0);
        temp.push(
            getLineDefaultValue(
                isEmpty(resolver_arg?.[0])
                    ? undefined
                    : (colIndex) => {
                          // 每一列 resolver_type 一致
                          return resolver_arg![0]![colIndex]?.resolver_type!;
                      }
            )
        );
        onChange(temp);
    };

    const renderAddBtn = () => {
        if (isView) {
            return;
        }
        return (
            <Button
                style={{ padding: 0, marginBottom: 2 }}
                type="text"
                icon={<PlusOutlined />}
                onClick={handleAdd}
            >
                {Intl.get('add')}
            </Button>
        );
    };

    const subform = field as VectorArgumentField;
    const drawerVisible = isNumber(expandRowIndex);

    const renderLoopContent = () => {
        return (
            <div className="loop-content">
                {map(vectorFields, (field, index) => {
                    const value = resolver_arg![0]![index]!;
                    // 获取子字段的匹配元数据（key 格式为 "vectorId.subFieldId"）
                    const subFieldMetadata =
                        autoFillMetadata?.[`${pluginComponentStore.field.id}.${field.id}`];
                    return (
                        <div className="loop-item" key={field.id}>
                            <CheckItem
                                tipClassName="error-tip"
                                required={field.isRequired}
                                tipVisible={checkTipVisible}
                                value={value.resolver_arg}
                            >
                                <PluginComponent
                                    className="argument-component"
                                    {...pluginComponentStore}
                                    field={field}
                                    fieldValue={value}
                                    resolverTypeList={resolverTypeList}
                                    optionalFields={optionalSubFields}
                                    resolverCtx={`${PLUGIN_ARGUMENT_VALUE_FROM.VECTOR}.`}
                                    autoFillMetadata={autoFillMetadata}
                                    matchMetadata={subFieldMetadata}
                                    onChange={(v) => {
                                        const temp = cloneDeep(resolver_arg!);
                                        temp[0]![index] = {
                                            ...temp[0]![index],
                                            ...v
                                        } as any as BaseArgumentValue;
                                        onChange(temp);
                                    }}
                                />
                            </CheckItem>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMapContent = () => {
        return (
            <>
                {renderAddBtn()}
                <MapVector
                    subform={subform}
                    value={resolver_arg}
                    resolverTypeList={resolverTypeList}
                    onRowExpand={setExpandRowIndex}
                />
            </>
        );
    };

    return (
        <div className="fx-plugin-vector">
            <div className="assignment-method">
                <div className="assignment-title">
                    {Intl.get('plugin.vector.assignment.method')}
                </div>
                <div
                    className={classNames('assignment-select', {
                        'is-map': !isLoop
                    })}
                >
                    <CustomCombo
                        isView={isView}
                        className="assignment-combo"
                        value={iterator_type}
                        onValueChange={(value: VectorIteratorType) => {
                            onChange(
                                value === VectorIteratorType.loop ? [getLineDefaultValue()] : [],
                                {
                                    iterator_type: value,
                                    resolver_ctx: undefined
                                }
                            );
                        }}
                        items={assignmentComboItems}
                        hasClear={false}
                        hasSearch={false}
                    />
                    <RightOutlined className="arrow-icon" />
                    {isLoop && (
                        <FieldVariableSelect
                            className="field-combo"
                            value={resolver_ctx}
                            onValueChange={(value) => {
                                onChange([getLineDefaultValue()], {
                                    resolver_ctx: value
                                });
                            }}
                            argumentType={PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR}
                        />
                    )}
                </div>
            </div>
            {isEmpty(vectorFields) ? (
                <div className="empty-tip">{Intl.get('vector.fields.empty')}</div>
            ) : isLoop ? (
                renderLoopContent()
            ) : (
                renderMapContent()
            )}
            <RowEditor
                visible={drawerVisible}
                title={subform.label}
                fields={subform.fieldConf?.fields}
                values={resolver_arg}
                rowIndex={expandRowIndex}
                onClose={() => {
                    setExpandRowIndex(undefined);
                }}
                onRowChange={setExpandRowIndex}
                onAdd={handleAdd}
            />
        </div>
    );
};

export default PluginVector;
